import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { QuizForm, type CourseSelectOption } from '@/components/quizzes/QuizForm'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useCourses } from '@/hooks/useCourses'
import { useCreateQuiz } from '@/hooks/useQuizzes'
import { paths } from '@/routes/path'
import BackButton from '@/components/ui/BackButton'

export function QuizCreatePage() {
  const navigate = useNavigate()
  const { data: coursesData, isLoading: isCoursesLoading } = useCourses({ limit: 200 })
  const createQuiz = useCreateQuiz()

  const courseOptions = useMemo<CourseSelectOption[]>(() => {
    const items = coursesData?.data ?? []
    return Array.isArray(items)
      ? items
          .map((course: any) => ({
            id: String(course.id),
            label: course.title ?? course.name ?? 'Untitled course',
            subtitle: course.code ?? undefined,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : []
  }, [coursesData])

  const handleSubmit = async (payload: any) => {
    const created = await createQuiz.mutateAsync(payload)
    toast.success('Quiz created successfully.')
    navigate(paths.app.quizzes.detail.build(created.id))
  }

  if (isCoursesLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!courseOptions.length) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-700">No courses found</CardTitle>
            <CardDescription className="text-sm text-amber-600">
              Please create a course before building a quiz. Each quiz must belong to a course.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">
            <div className="flex items-center gap-2">
              <BackButton/>
              <span>Create a new quiz</span>
            </div>
          </CardTitle>
          <CardDescription>
            Set the basics now and add questions later. Learners will only see the quiz once you
            publish it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <QuizForm
            courses={courseOptions}
            onSubmit={handleSubmit}
            isSubmitting={createQuiz.isPending}
            submitLabel="Create quiz"
          />
        </CardContent>
      </Card>
    </div>
  )
}
